'use client';

import { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface ProductHeroProps {
  product: {
    name: string;
    tagline: string;
    images: Array<{
      url: string;
      alt: string;
      angle: string;
    }>;
    highlights: string[];
  };
}

export default function ProductHero({ product }: ProductHeroProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % product.images.length);
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + product.images.length) % product.images.length);
  };

  return (
    <section className="relative bg-gradient-to-b from-[#f5fbf6] to-background py-20 lg:py-24 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 bg-accent/10 text-accent px-4 py-2 rounded-full border border-accent/20">
              <Icon name="ShieldCheckIcon" size={18} className="text-accent" />
              <span className="text-sm font-semibold">Product built for trusted communication</span>
            </div>

            <div className="space-y-4">
              <h1 className="text-4xl lg:text-6xl font-heading font-extrabold text-primary leading-tight">
                {product.name}
              </h1>
              <p className="text-xl lg:text-2xl text-muted-foreground font-medium">
                {product.tagline}
              </p>
            </div>

            <div className="space-y-4">
              {product.highlights.map((highlight, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-accent/10 rounded-full border border-accent/20 flex items-center justify-center mt-1">
                    <Icon name="CheckIcon" size={16} className="text-accent" />
                  </div>
                  <p className="text-foreground text-base">{highlight}</p>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-4 pt-2">
              <Link
                href="/get-started"
                className="px-8 py-4 bg-accent text-accent-foreground font-heading font-semibold rounded-xl shadow-cta hover:shadow-cta-hover hover:-translate-y-1 transition-all duration-300"
              >
                Get ParkPing
              </Link>
              <Link
                href="/how-it-works"
                className="px-8 py-4 bg-white text-primary font-heading font-semibold rounded-xl border border-border hover:bg-muted transition-all duration-300"
              >
                See How It Works
              </Link>
            </div>
          </div>

          <div className="relative">
            <div className="relative bg-card rounded-2xl p-8 border border-border shadow-card">
              <div className="relative aspect-square overflow-hidden rounded-xl bg-muted/40 border border-border">
                <AppImage
                  src={product.images[currentImageIndex].url}
                  alt={product.images[currentImageIndex].alt}
                  className="w-full h-full object-contain"
                />
              </div>

              <div className="absolute top-1/2 -translate-y-1/2 left-2 right-2 flex justify-between">
                <button
                  onClick={handlePrevImage}
                  className="w-11 h-11 bg-white rounded-full border border-border flex items-center justify-center shadow-subtle hover:scale-105 transition-all duration-300"
                  aria-label="Previous image"
                >
                  <Icon name="ChevronLeftIcon" size={20} className="text-primary" />
                </button>
                <button
                  onClick={handleNextImage}
                  className="w-11 h-11 bg-white rounded-full border border-border flex items-center justify-center shadow-subtle hover:scale-105 transition-all duration-300"
                  aria-label="Next image"
                >
                  <Icon name="ChevronRightIcon" size={20} className="text-primary" />
                </button>
              </div>

              <div className="mt-6 flex justify-center space-x-3">
                {product.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                      currentImageIndex === index
                        ? 'border-accent scale-105 shadow-subtle'
                        : 'border-border hover:border-accent/50'
                    }`}
                  >
                    <AppImage
                      src={image.url}
                      alt={image.alt}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>

              <div className="mt-4 text-center">
                <p className="text-sm text-muted-foreground">
                  {product.images[currentImageIndex].angle}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
